import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Send, Filter, CreditCard, Lock, Unlock, CalendarClock, Search } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import MessageFiles from '../../components/MessageFiles';
import LessonFiles from '../../components/LessonFiles';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import PasswordInput from '../../components/PasswordInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function MessagesTab({ ctx }) {
  const {
    token,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  } = ctx;
  return (
          <TabsContent value="messages" data-testid="messages-content">
            <Tabs defaultValue="board" className="space-y-6">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="board">Информационно Табло</TabsTrigger>
                <TabsTrigger value="private">Лични Съобщения</TabsTrigger>
              </TabsList>

              <TabsContent value="board">
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">Изпращане на съобщение до клас</h2>
                    <Card className="p-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="boardClass">Клас</Label>
                          <Select value={boardClassId} onValueChange={setBoardClassId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Изберете клас" />
                            </SelectTrigger>
                            <SelectContent>
                              {classes.map((cls) => (
                                <SelectItem key={cls.class_id} value={cls.class_id}>
                                  {cls.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <RichTextEditor
                          value={boardContent}
                          onChange={setBoardContent}
                          placeholder="Напишете вашето съобщение..."
                        />

                        <FilePickerInput
                          files={boardFiles}
                          onChange={setBoardFiles}
                          label="Прикачи файлове"
                        />

                        <Button
                          onClick={handleSendBoardMessage}
                          className="w-full bg-[#00966E] hover:bg-[#007a59] text-white flex items-center gap-2"
                        >
                          <Send className="h-4 w-4" />
                          Изпращане
                        </Button>
                      </div>
                    </Card>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <h2 className="text-xl font-semibold text-slate-900">Съобщения от информационното табло</h2>
                      <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                        <div className="relative flex-1 max-w-[240px]">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                          <Input
                            data-testid="admin-message-search-input"
                            value={messageSearch}
                            onChange={(e) => setMessageSearch(e.target.value)}
                            placeholder="Търсене в съобщения..."
                            className="pl-8 h-9 text-sm"
                          />
                        </div>
                        <Select value={boardFilter} onValueChange={setBoardFilter}>
                          <SelectTrigger className="w-[130px] sm:w-[160px]">
                            <Filter className="h-4 w-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Всички</SelectItem>
                            <SelectItem value="student">Ученици</SelectItem>
                            <SelectItem value="teacher">Учители</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {boardMessages.filter(msg => {
                      if (boardFilter !== 'all' && msg.sender_role !== boardFilter) return false;
                      if (!messageSearch) return true;
                      const needle = messageSearch.toLowerCase();
                      const hay = `${msg.sender_name || ''} ${(msg.content || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
                      return hay.includes(needle);
                    }).length === 0 ? (
                      <Card className="p-8 text-center text-slate-500">
                        Няма съобщения
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {boardMessages.filter(msg => {
                          if (boardFilter !== 'all' && msg.sender_role !== boardFilter) return false;
                          if (!messageSearch) return true;
                          const needle = messageSearch.toLowerCase();
                          const hay = `${msg.sender_name || ''} ${(msg.content || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
                          return hay.includes(needle);
                        }).map((msg) => (
                          <Card key={msg.message_id} className="p-4 border border-slate-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">{msg.sender_name}</span>
                                <Badge className={getSenderRoleColor(msg.sender_role)}>
                                  {msg.sender_role === 'teacher' ? 'Учител' : msg.sender_role === 'administrator' ? 'Администратор' : 'Ученик'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">
                                  {new Date(msg.created_at).toLocaleDateString('bg-BG')}
                                </span>
                                <Button
                                  onClick={() => handleEditMessage(msg)}
                                  variant="ghost" size="sm"
                                  data-testid="edit-board-message-btn"
                                  className="text-slate-500 hover:text-[#00966E] hover:bg-green-50 h-6 w-6 p-0"
                                ><Edit className="h-3 w-3" /></Button>
                                <Button
                                  onClick={() => handleDeleteMessage(msg.message_id)}
                                  variant="ghost"
                                  size="sm"
                                  data-testid="delete-message-btn"
                                  className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 h-6 w-6 p-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-slate-700 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                            <YouTubeEmbed html={msg.content} />
                            <MessageFiles msg={msg} token={token} />
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="private">
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">Изпращане на лично съобщение</h2>
                    <Card className="p-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="recipient">Получател</Label>
                          <Select value={privateRecipient} onValueChange={setPrivateRecipient}>
                            <SelectTrigger>
                              <SelectValue placeholder="Изберете получател" />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                              {recipients.filter(r => r.role === 'student').length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">Ученици</div>
                                  {recipients.filter(r => r.role === 'student').map(r => (
                                    <SelectItem key={r.user_id} value={r.user_id} className="pl-6">
                                      {r.username}
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                              {recipients.filter(r => r.role === 'teacher').length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0 mt-1">Учители</div>
                                  {recipients.filter(r => r.role === 'teacher').map(r => (
                                    <SelectItem key={r.user_id} value={r.user_id} className="pl-6">
                                      {r.username}
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <RichTextEditor
                          value={privateContent}
                          onChange={setPrivateContent}
                          placeholder="Напишете вашето съобщение..."
                        />
                        <FilePickerInput
                          files={privateFiles}
                          onChange={setPrivateFiles}
                          label="Прикачи файлове"
                        />
                        <Button
                          onClick={handleSendPrivateMessage}
                          className="w-full bg-[#00966E] hover:bg-[#007a59] text-white flex items-center justify-center gap-2"
                        >
                          <Send className="h-4 w-4" />
                          Изпращане
                        </Button>
                      </div>
                    </Card>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <h2 className="text-xl font-semibold text-slate-900">Получени лични съобщения</h2>
                      <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                        <div className="relative flex-1 max-w-[240px]">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                          <Input
                            data-testid="admin-private-search-input"
                            value={messageSearch}
                            onChange={(e) => setMessageSearch(e.target.value)}
                            placeholder="Търсене в съобщения..."
                            className="pl-8 h-9 text-sm"
                          />
                        </div>
                        <Select value={privateFilter} onValueChange={setPrivateFilter}>
                          <SelectTrigger className="w-[130px] sm:w-[160px]">
                            <Filter className="h-4 w-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Всички</SelectItem>
                            <SelectItem value="student">Ученици</SelectItem>
                            <SelectItem value="teacher">Учители</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {privateMessages.filter(msg => {
                      if (privateFilter !== 'all' && msg.sender_role !== privateFilter) return false;
                      if (!messageSearch) return true;
                      const needle = messageSearch.toLowerCase();
                      const hay = `${msg.sender_name || ''} ${(msg.content || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
                      return hay.includes(needle);
                    }).length === 0 ? (
                      <Card className="p-8 text-center text-slate-500">
                        Няма лични съобщения
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {privateMessages.filter(msg => {
                          if (privateFilter !== 'all' && msg.sender_role !== privateFilter) return false;
                          if (!messageSearch) return true;
                          const needle = messageSearch.toLowerCase();
                          const hay = `${msg.sender_name || ''} ${(msg.content || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
                          return hay.includes(needle);
                        }).map((msg) => (
                          <Card key={msg.message_id} className="p-4 border border-slate-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">{msg.sender_name}</span>
                                <Badge className={getSenderRoleColor(msg.sender_role)}>
                                  {msg.sender_role === 'teacher' ? 'Учител' : msg.sender_role === 'administrator' ? 'Администратор' : 'Ученик'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">
                                  {new Date(msg.created_at).toLocaleDateString('bg-BG')}
                                </span>
                                <Button
                                  onClick={() => handleEditMessage(msg)}
                                  variant="ghost" size="sm"
                                  data-testid="edit-private-message-btn"
                                  className="text-slate-500 hover:text-[#00966E] hover:bg-green-50 h-6 w-6 p-0"
                                ><Edit className="h-3 w-3" /></Button>
                                <Button
                                  onClick={() => handleDeleteMessage(msg.message_id)}
                                  variant="ghost"
                                  size="sm"
                                  data-testid="delete-private-message-btn"
                                  className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 h-6 w-6 p-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-slate-700 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                            <YouTubeEmbed html={msg.content} />
                            <MessageFiles msg={msg} token={token} />
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
  );
}
